import { HttpClient } from '@angular/common/http';
import { Injectable , inject} from '@angular/core';
import { AppConfigService } from 'app/core/config/app-config.service';
import { Notification, ApiNotification } from 'app/layout/common/notifications/notifications.types';
import { MessageCountersService } from 'app/services/message-counters.service';
import { BehaviorSubject, catchError, map, Observable, of, ReplaySubject, tap } from 'rxjs';
import { environment } from '@env/environment';
import { TranslateService } from '@ngx-translate/core';
import { isNotificationDebugEnabled } from 'app/core/debug/notification-debug';

export interface NotificationListResult {
    error?: string;
    items: Notification[];
    total: number;
    page: number;
    perPage: number;
    view: 'open' | 'waiting' | 'all';
}

@Injectable({providedIn: 'root'})
export class NotificationsService
{
  readonly appConfig = inject(AppConfigService);
    private translate = inject(TranslateService);
    private _notifications: ReplaySubject<Notification[]> = new ReplaySubject<Notification[]>(1);
    private _unreadCount: BehaviorSubject<number> = new BehaviorSubject<number>(0);

    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private _messageCountersService: MessageCountersService,
    )
    {
    }

    private _debug(label: string, payload?: any, color: string = '#7c3aed'): void
    {
        if (!isNotificationDebugEnabled()) {
            return;
        }
        try {
            // eslint-disable-next-line no-console
            console.log(`%c[NotificationsService] ${label}`, `color:${color};font-weight:600`, payload ?? '');
        } catch {
            // no-op
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for notifications
     */
    get notifications$(): Observable<Notification[]>
    {
        return this._notifications.asObservable();
    }

    /**
     * Getter for unread count
     */
    get unreadCount$(): Observable<number>
    {
        return this._unreadCount.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get all notifications from the real API
     */
    getAll(options?: { view?: 'open' | 'waiting' | 'all'; page?: number; perPage?: number; companyId?: number | null }): Observable<NotificationListResult>
    {
        const view = options?.view ?? 'open';
        const page = options?.page ?? 1;
        const perPage = options?.perPage ?? 20;
        const companyId = options?.companyId ?? null;
        let url = `${this.appConfig.get('serverUrl')}/v1/notifications?view=${view}&page=${page}&per_page=${perPage}`;
        if (companyId && companyId > 0) {
            url += `&company_id=${companyId}`;
        }
        this._debug('getAll() request', { view, page, perPage, companyId, url }, '#2563eb');

        return this._httpClient.get<any>(url).pipe(
            map((response) => {
                this._debug('getAll() raw response', response, '#1d4ed8');
                if (response?.status === 'OK' && Array.isArray(response.data)) {
                    const items = response.data.map((apiNotif: ApiNotification) => this._mapApiToNotification(apiNotif));
                    this._debug('getAll() mapped result', {
                        view,
                        page: Number(response?.page ?? page),
                        perPage: Number(response?.per_page ?? perPage),
                        total: Number(response?.total ?? items.length),
                        items: items.length,
                    }, '#0f766e');
                    return {
                        items,
                        total: Number(response?.total ?? items.length),
                        page: Number(response?.page ?? page),
                        perPage: Number(response?.per_page ?? perPage),
                        view,
                    } as NotificationListResult;
                }
                return {
                    items: [],
                    total: 0,
                    page,
                    perPage,
                    view,
                } as NotificationListResult;
            }),
            catchError((error) => {
                this._debug('getAll() failed', error, '#dc2626');
                return of({
                    error: this.translate.instant('notifications.loadError'),
                    items: [],
                    total: 0,
                    page,
                    perPage,
                    view,
                } as NotificationListResult);
            }),
        );
    }

    /**
     * Get count of notifications waiting for student response (view=waiting).
     */
    getWaitingCount(companyId?: number | null): Observable<number>
    {
        if (companyId && companyId > 0) {
            return this.getAll({ view: 'waiting', page: 1, perPage: 1, companyId }).pipe(
                map((result) => Number(result.total || 0)),
                catchError(() => of(0)),
            );
        }

        return this._messageCountersService.getCounters().pipe(
            map((counters) => Number(counters?.totals?.waiting_for_student || 0)),
            catchError(() => of(0)),
        );
    }

    /**
     * Get unread notification count aligned with the notifications panel.
     * When companyId is set, uses the open-notifications list total (same filter as the panel).
     * Otherwise uses the global counters endpoint.
     */
    getUnreadCount(companyId?: number | null): Observable<number>
    {
        if (companyId && companyId > 0) {
            this._debug('getUnreadCount() company-scoped request', { companyId }, '#a21caf');
            return this.getAll({ view: 'open', page: 1, perPage: 1, companyId }).pipe(
                map((result) => {
                    const count = Number(result.total || 0);
                    this._unreadCount.next(count);
                    this._debug('getUnreadCount() company-scoped response', { companyId, count }, '#7e22ce');
                    return count;
                }),
                catchError((error) => {
                    this._debug('getUnreadCount() company-scoped failed', error, '#dc2626');
                    return of(this._unreadCount.getValue());
                }),
            );
        }

        return this._messageCountersService.getCounters().pipe(
            map((counters) => {
                const count = Number(counters?.totals?.notifications_needs_action || 0);
                this._unreadCount.next(count);
                this._debug('getUnreadCount() global response', {
                    notifications_needs_action: count,
                    totals: counters?.totals || {},
                }, '#7e22ce');
                return count;
            }),
            catchError((error) => {
                this._debug('getUnreadCount() global failed', error, '#dc2626');
                return of(this._unreadCount.getValue());
            }),
        );
    }

    /**
     * Create a notification (not used with real API - notifications are created server-side)
     */
    create(notification: Notification): Observable<Notification>
    {
        // With the real API, notifications are created server-side.
        // This is kept for backwards compatibility but is essentially a no-op.
        return of(notification);
    }

    /**
     * Update the notification (toggle read status)
     */
    update(id: string, notification: Notification): Observable<Notification>
    {
        this._debug('update() start', {
            id,
            read: notification?.read,
            attention_state: notification?.attention_state,
        }, '#ea580c');

        if (!notification.read) {
            return of(notification);
        }

        const url = `${this.appConfig.get('serverUrl')}/v1/notifications/mark-read`;
        const payload: any = { ids: [parseInt(id, 10)] };
        if (notification.student_course_step_id) {
            payload.step_ids = [notification.student_course_step_id];
        }
        this._debug('update() mark-read request', { id, url, payload }, '#c2410c');
        return this._httpClient.post<any>(url, payload).pipe(
            map((res) => {
                const directUpdated = Number(res?.data?.updated ?? 0);
                const siblingUpdated = Number(res?.data?.sibling_updated ?? 0);
                const totalResolved = Math.max(directUpdated, siblingUpdated);
                const skipped = Number(res?.data?.skipped ?? 0);
                this._debug('update() mark-read response', {
                    id,
                    updated: directUpdated,
                    sibling_updated: siblingUpdated,
                    skipped,
                    totalResolved,
                    status: res?.status,
                }, '#9a3412');
                if (skipped > 0 && totalResolved === 0) {
                    return { ...notification, read: false };
                }
                if (totalResolved > 0) {
                    const current = this._unreadCount.getValue();
                    const next = Math.max(0, current - totalResolved);
                    this._unreadCount.next(next);
                    this._debug('update() unread count optimistic update', { from: current, to: next, resolved: totalResolved }, '#047857');
                } else {
                    const current = this._unreadCount.getValue();
                    this._unreadCount.next(Math.max(0, current - 1));
                }
                return notification;
            }),
            catchError((err) => {
                this._debug('update() mark-read failed', { id, err }, '#dc2626');
                return of(notification);
            }),
        );
    }

    /**
     * Delete the given notification (mark as read and hide locally)
     */
    delete(id: string): Observable<boolean>
    {
        this._debug('delete() start', { id }, '#be123c');
        const url = `${this.appConfig.get('serverUrl')}/v1/notifications/mark-read`;
        return this._httpClient.post<any>(url, { ids: [parseInt(id, 10)] }).pipe(
            map((res) => {
                this._debug('delete() mark-read response', { id, res }, '#9f1239');
                const current = this._unreadCount.getValue();
                this._unreadCount.next(Math.max(0, current - 1));
                return true;
            }),
            catchError((err) => {
                this._debug('delete() mark-read failed', { id, err }, '#dc2626');
                return of(true);
            }),
        );
    }

    /**
     * Mark all notifications as read
     */
    markAllAsRead(): Observable<boolean>
    {
        const url = `${this.appConfig.get('serverUrl')}/v1/notifications/mark-read`;
        this._debug('markAllAsRead() request', { url }, '#065f46');

        return this._httpClient.post<any>(url, { all: true }).pipe(
            tap(() => {
                this._unreadCount.next(0);
                this._debug('markAllAsRead() local counter cleared', {}, '#047857');
            }),
            map(() => true),
            catchError((error) => {
                this._debug('markAllAsRead() failed', error, '#dc2626');
                return of(false);
            }),
        );
    }

    /**
     * Mark all notifications related to a specific student course step as done/read.
     * Called automatically when a supervisor approves or rejects a step.
     */
    markDoneByStep(studentCourseStepId: number): Observable<boolean>
    {
        this._debug('markDoneByStep() start', { studentCourseStepId }, '#0369a1');
        if (!studentCourseStepId || studentCourseStepId <= 0) {
            return of(false);
        }

        const url = `${this.appConfig.get('serverUrl')}/v1/notifications/mark-read`;
        this._debug('markDoneByStep() request', { studentCourseStepId, url }, '#0284c7');
        return this._httpClient.post<any>(url, { step_ids: [studentCourseStepId] }).pipe(
            map((res) => {
                const directUpdated = Number(res?.data?.updated ?? 0);
                const siblingUpdated = Number(res?.data?.sibling_updated ?? 0);
                const totalResolved = Math.max(directUpdated, siblingUpdated);
                if (totalResolved > 0) {
                    const current = this._unreadCount.getValue();
                    this._unreadCount.next(Math.max(0, current - totalResolved));
                }
                this._debug('markDoneByStep() response', { studentCourseStepId, totalResolved, res }, '#0e7490');
                return true;
            }),
            catchError((err) => {
                this._debug('markDoneByStep() failed', { studentCourseStepId, err }, '#dc2626');
                return of(true);
            }),
        );
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Human-readable action type labels
     */
    private static readonly ACTION_TYPE_LABELS: { [key: number]: string } = {
        1: 'notifications.type.1',
        2: 'notifications.type.2',
        3: 'notifications.type.3',
        4: 'notifications.type.4',
        5: 'notifications.type.5',
        6: 'notifications.type.6',
        7: 'notifications.type.7',
        8: 'notifications.type.8',
        9: 'notifications.type.9',
        10: 'notifications.type.10',
        11: 'notifications.type.11',
        12: 'notifications.type.12',
        13: 'notifications.type.13',
        14: 'notifications.type.14',
        15: 'notifications.type.15',
        16: 'notifications.type.16',
        17: 'notifications.type.17',
        18: 'notifications.type.18',
        21: 'notifications.type.21',
        22: 'notifications.type.22',
    };

    /**
     * Map API notification data to the Notification interface
     */
    private _mapApiToNotification(apiNotif: ApiNotification): Notification
    {
        let icon = 'heroicons_outline:bell';
        let link: string | undefined;
        let useRouter = false;

        // Set icon based on message_type
        switch (apiNotif.message_type) {
            case 'student_submission':
                icon = 'heroicons_outline:document-text';
                break;
            case 'step_approval':
                icon = 'heroicons_outline:check-circle';
                break;
            case 'step_rejection':
                icon = 'heroicons_outline:x-circle';
                break;
            case 'system_notification':
                icon = 'heroicons_outline:bell-alert';
                break;
            // LMS notification types
            case 'badge_earned':
                icon = 'heroicons_outline:star';
                break;
            case 'course_completed':
                icon = 'heroicons_outline:trophy';
                break;
            case 'course_auto_enrolled':
                icon = 'heroicons_outline:rocket-launch';
                break;
            case 'nachschulung_triggered':
                icon = 'heroicons_outline:exclamation-triangle';
                break;
            case 'nachschulung_completed':
                icon = 'heroicons_outline:check-badge';
                break;
            case 'study_plan_assigned':
                icon = 'heroicons_outline:academic-cap';
                break;
            case 'study_plan_completed':
                icon = 'heroicons_outline:academic-cap';
                break;
            case 'certificate_issued':
                icon = 'heroicons_outline:document-text';
                break;
            case 'prerequisite_met':
                icon = 'heroicons_outline:lock-open';
                break;
            case 'recurring_course_due':
                icon = 'heroicons_outline:clock';
                break;
            case 'rating_updated':
                icon = 'heroicons_outline:chart-bar';
                break;
            case 'student_win':
                icon = 'heroicons_outline:trophy';
                break;
        }

        // ── Build descriptive title ──────────────────────
        // Show student name as the primary title (most important for supervisors)
        let title = apiNotif.student_name || this.translate.instant('Student');

        // ── Build rich description ───────────────────────
        // Format: "Course Name — Step #N: Step Title (Type)"
        const descParts: string[] = [];

        if (apiNotif.course_title) {
            descParts.push(apiNotif.course_title);
        }

        // Step number + title
        const stepNum = (apiNotif as any).step_sort;
        if (stepNum && apiNotif.step_title) {
            descParts.push(this.translate.instant('notifications.stepNumberTitle', { n: stepNum, title: apiNotif.step_title }));
        } else if (stepNum) {
            descParts.push(this.translate.instant('notifications.stepNumber', { n: stepNum }));
        } else if (apiNotif.step_title) {
            descParts.push(apiNotif.step_title);
        }

        // Action type label
        if (apiNotif.actiontype_id) {
            const typeLabel = NotificationsService.ACTION_TYPE_LABELS[apiNotif.actiontype_id];
            if (typeLabel) {
                descParts.push(this.translate.instant(typeLabel));
            }
        }

        let description = descParts.join(' — ');

        // Fallback to message content if nothing else
        if (!description && apiNotif.message) {
            const plainText = apiNotif.message.replace(/<[^>]*>/g, '').trim();
            description = plainText.length > 120 ? plainText.substring(0, 120) + '...' : plainText;
        }

        // Build link based on notification type
        switch (apiNotif.message_type) {
            case 'badge_earned':
                if (apiNotif.badge_id) {
                    link = `/badges-mgmt/${apiNotif.badge_id}`;
                    useRouter = true;
                }
                break;
            case 'study_plan_assigned':
            case 'study_plan_completed':
                if (apiNotif.student_id) {
                    link = `/students/${apiNotif.student_id}`;
                    useRouter = true;
                }
                break;
            case 'nachschulung_triggered':
            case 'nachschulung_completed':
                if (apiNotif.student_id && apiNotif.student_course_id) {
                    link = `/supervisor/${apiNotif.student_id}/${apiNotif.student_course_id}`;
                    useRouter = true;
                }
                break;
            case 'course_auto_enrolled':
                if (apiNotif.student_id && apiNotif.student_course_id) {
                    link = `/supervisor/${apiNotif.student_id}/${apiNotif.student_course_id}`;
                    useRouter = true;
                } else if (apiNotif.student_id) {
                    link = `/students/${apiNotif.student_id}`;
                    useRouter = true;
                }
                break;
            case 'certificate_issued':
                link = `/certificates`;
                useRouter = true;
                break;
            case 'recurring_course_due':
                if (apiNotif.student_id) {
                    link = `/students/${apiNotif.student_id}`;
                    useRouter = true;
                }
                break;
            case 'student_win':
                if (apiNotif.student_id) {
                    link = `/students/${apiNotif.student_id}`;
                    useRouter = true;
                }
                break;
            default:
                if (apiNotif.student_id && apiNotif.student_course_id) {
                    link = `/supervisor/${apiNotif.student_id}/${apiNotif.student_course_id}`;
                    useRouter = true;
                }
                break;
        }

        return {
            id: String(apiNotif.t_ot_messagesid),
            icon,
            title,
            description,
            time: apiNotif.created,
            link,
            useRouter,
            read: apiNotif.status_id !== 1, // 1 = UNREAD
            message_type: apiNotif.message_type,
            attention_state: apiNotif.attention_state,
            ack_required: apiNotif.ack_required,
            ack_at: apiNotif.ack_at,
            delivered_at: apiNotif.delivered_at,
            read_at: apiNotif.read_at,
            student_name: apiNotif.student_name,
            company_name: apiNotif.company_name,
            course_title: apiNotif.course_title,
            step_title: apiNotif.step_title,
            student_id: apiNotif.student_id,
            student_course_id: apiNotif.student_course_id,
            student_course_step_id: apiNotif.student_course_step_id,
            step_id: apiNotif.step_id,
            step_sort: (apiNotif as any).step_sort,
            actiontype_id: apiNotif.actiontype_id,
            company_id: apiNotif.company_id,
            badge_id: apiNotif.badge_id,
            badge_title: apiNotif.badge_title,
            rating_tier: apiNotif.rating_tier,
            certificate_id: apiNotif.certificate_id,
            study_plan_id: apiNotif.study_plan_id,
            group_id: apiNotif.group_id,
        };
    }
}
